import { decrypt, decryptLegacy, encrypt, getEncryptionKey, isCipher, isLegacyCipher, } from '@famly/stat_ts-utils_cryptography';
import { KEYPART_KEY } from '@famly/stat_ts-utils_famlytics';
import { getAllKeys } from './get-all-keys';
import { getFromLocalStorage } from './get-from-local-storage';
import { loginId, secret } from './local-storage-vars';
import { saveToLocalStorage } from './save-to-local-storage';
/*
 * Web Crypto only decrypts asynchronously, but the encrypted getters are read while rendering. So a user's
 * encrypted values are decrypted into memory once they log in, reads come from memory, and writes update memory
 * at once and reach localStorage when their encryption finishes.
 */
const STORAGE_PREFIX = 'famly.';
let owner;
let loading = Promise.resolve();
let isListeningToOtherTabs = false;
// Kept as JSON so every read gets its own copy, as it would parsing localStorage.
const values = new Map();
const encryptionKeys = new Map();
// A write or clear supersedes any pending write, and any pending decryption, of the same key.
let changeCount = 0;
const latestChange = new Map();
const isCurrentOwner = (candidate) => candidate !== undefined && candidate.loginId === loginId && candidate.secret === secret;
const markChanged = (storageKey) => {
    changeCount += 1;
    latestChange.set(storageKey, changeCount);
    return changeCount;
};
const getKey = (keyMaterial, keyOwner) => {
    let key = encryptionKeys.get(keyMaterial);
    if (!key) {
        key = getEncryptionKey(keyOwner.secret, keyMaterial);
        encryptionKeys.set(keyMaterial, key);
    }
    return key;
};
const readStored = (storageKey) => {
    try {
        return getFromLocalStorage(storageKey);
    }
    catch (_a) {
        return undefined;
    }
};
// Callers pick between the login id and the key part when they write, but not every caller that reads knows
// which one, so reading tries both.
const getKeyMaterials = (keyOwner) => {
    const keyParts = readStored(KEYPART_KEY);
    return [keyOwner.loginId, keyParts === null || keyParts === void 0 ? void 0 : keyParts[keyOwner.loginId]].filter((keyMaterial) => typeof keyMaterial === 'string');
};
const decryptForOwner = async (cipher, cipherOwner) => {
    const keyMaterials = getKeyMaterials(cipherOwner);
    for (const keyMaterial of keyMaterials) {
        const value = await decrypt(cipher, await getKey(keyMaterial, cipherOwner));
        if (value !== undefined) {
            return value;
        }
    }
    return undefined;
};
const decryptIntoMemory = async (storageKey, cipher, cipherOwner) => {
    const change = markChanged(storageKey);
    values.delete(storageKey);
    const value = await decryptForOwner(cipher, cipherOwner);
    if (value !== undefined && owner === cipherOwner && latestChange.get(storageKey) === change) {
        values.set(storageKey, JSON.stringify(value));
    }
};
// Re-encrypts a value crypto-js wrote with the same key material, or removes it when it does not decrypt.
const migrateLegacyValue = async (storageKey, cipher, migrationOwner) => {
    const change = markChanged(storageKey);
    for (const keyMaterial of getKeyMaterials(migrationOwner)) {
        const value = await decryptLegacy(cipher, migrationOwner.secret, keyMaterial);
        if (value !== undefined) {
            const migrated = await encrypt(value, await getKey(keyMaterial, migrationOwner));
            if (owner === migrationOwner && latestChange.get(storageKey) === change) {
                saveToLocalStorage(storageKey, migrated);
                values.set(storageKey, JSON.stringify(value));
            }
            return;
        }
    }
    if (latestChange.get(storageKey) === change) {
        window.localStorage.removeItem(`${STORAGE_PREFIX}${storageKey}`);
    }
};
const loadStoredValues = async (loadOwner) => {
    const userKeyPrefix = `${STORAGE_PREFIX}${loadOwner.loginId}.`;
    const storageKeys = getAllKeys()
        .filter(key => key.startsWith(userKeyPrefix))
        .map(key => key.slice(STORAGE_PREFIX.length));
    await Promise.all(storageKeys.map(async (storageKey) => {
        const stored = readStored(storageKey);
        if (isLegacyCipher(stored)) {
            await migrateLegacyValue(storageKey, stored, loadOwner);
        }
        else if (isCipher(stored)) {
            await decryptIntoMemory(storageKey, stored, loadOwner);
        }
    }));
};
const listenToOtherTabs = () => {
    if (isListeningToOtherTabs) {
        return;
    }
    isListeningToOtherTabs = true;
    window.addEventListener('storage', ({ key }) => {
        if (!isCurrentOwner(owner)) {
            return;
        }
        if (key === null) {
            values.clear();
            return;
        }
        if (!key.startsWith(`${STORAGE_PREFIX}${owner.loginId}.`)) {
            return;
        }
        const storageKey = key.slice(STORAGE_PREFIX.length);
        const stored = readStored(storageKey);
        if (isCipher(stored)) {
            decryptIntoMemory(storageKey, stored, owner);
        }
        else if (isLegacyCipher(stored)) {
            // A tab still running the app from before the switch to Web Crypto
            values.delete(storageKey);
            migrateLegacyValue(storageKey, stored, owner);
        }
        else {
            markChanged(storageKey);
            values.delete(storageKey);
        }
    });
};
/**
 * Decrypts the encrypted values of the user set with `setLoginIdForLocalStorage` and `setSecretForLocalStorage`, so
 * the encrypted getters can return them. Until it resolves, they return `undefined`. Values in the earlier crypto-js
 * format are re-encrypted on the way. Never rejects; a value that does not decrypt reads as missing.
 */
export const loadEncryptedValues = () => {
    if (!loginId || !secret || isCurrentOwner(owner)) {
        return loading;
    }
    const loadOwner = { loginId, secret };
    owner = loadOwner;
    values.clear();
    encryptionKeys.clear();
    latestChange.clear();
    listenToOtherTabs();
    loading = loadStoredValues(loadOwner).catch(() => undefined);
    return loading;
};
export const readEncryptedValue = (storageKey) => {
    const json = isCurrentOwner(owner) ? values.get(storageKey) : undefined;
    return json === undefined ? undefined : JSON.parse(json);
};
/**
 * Makes `payload` readable at once and saves it to localStorage, encrypted with `keyMaterial`, once Web Crypto
 * finishes. The save is dropped if the key changes again first, or the user logs out.
 * @returns a promise of false when the value could not be stored; one replaced by a later change counts as stored
 */
export const writeEncryptedValue = (storageKey, payload, keyMaterial) => {
    if (!isCurrentOwner(owner)) {
        loadEncryptedValues();
    }
    if (!isCurrentOwner(owner)) {
        return Promise.resolve(false);
    }
    const writeOwner = owner;
    const change = markChanged(storageKey);
    values.set(storageKey, JSON.stringify(payload));
    return getKey(keyMaterial, writeOwner)
        .then(key => encrypt(payload, key))
        .then(cipher => {
        if (!isCurrentOwner(writeOwner) || owner !== writeOwner) {
            return false;
        }
        return latestChange.get(storageKey) !== change || saveToLocalStorage(storageKey, cipher);
    })
        .catch(() => false);
};
export const forgetEncryptedValue = (storageKey) => {
    markChanged(storageKey);
    values.delete(storageKey);
};
